import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Phone,
  Mail,
  FileText,
  UserCircle,
  ShieldCheck,
  Sparkles,
  Pencil,
  Plus,
  Clock,
  Building2,
  Globe,
  MapPin,
  AtSign,
} from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { toNationalDisplay } from '@shared/phone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AppProfileEditDialog } from '@/components/app-profile-edit-dialog';
import { AppCompanyEditDialog } from '@/components/app-company-edit-dialog';
import { SetAccessCard } from '@/components/set-access-card';
import { AppFiscalProfileCard } from '@/components/app-fiscal-profile-card';
import { AppRucAddDialog } from '@/components/app-ruc-add-dialog';
import { AppRucEditDialog } from '@/components/app-ruc-edit-dialog';

export function AppAccountPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading } = useTenantMe();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showAddRuc, setShowAddRuc] = useState(false);
  const [showEditRuc, setShowEditRuc] = useState(false);

  if (isLoading) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div>
        <h1 className="text-2xl font-bold">Mi cuenta</h1>
        <p className="text-sm text-muted-foreground">
          Tus datos personales y de contacto registrados en Actuo.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCircle className="size-4" />
            Datos personales
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowEditProfile(true)}>
            <Pencil />
            Editar
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <Row icon={<UserCircle className="size-4" />} label="Nombre">
            {me.tenant.fullName}
          </Row>
          <Row icon={<Phone className="size-4" />} label="WhatsApp">
            <span className="font-mono">{toNationalDisplay(me.tenant.whatsappNumber)}</span>
          </Row>
          {me.tenant.email && (
            <Row icon={<Mail className="size-4" />} label="Email">
              {me.tenant.email}
            </Row>
          )}

          <Separator className="my-2" />

          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Identidad fiscal
            </span>
            {me.rucs.length === 0 ? (
              <Button variant="ghost" size="xs" onClick={() => setShowAddRuc(true)}>
                <Plus />
                Cargar RUC
              </Button>
            ) : !me.rucs[0]?.validatedAt ? (
              <Button variant="ghost" size="xs" onClick={() => setShowEditRuc(true)}>
                <Pencil />
                Editar
              </Button>
            ) : null}
          </div>

          {me.rucs.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Pendiente de carga. Avisale a tu contador, o usá "Cargar RUC" para hacerlo vos.
            </p>
          ) : (
            <>
              <Row icon={<FileText className="size-4" />} label="RUC">
                <span className="font-mono">{me.rucs[0]!.ruc}</span>
              </Row>
              <Row label="Razón social">{me.rucs[0]!.razonSocial}</Row>
              <Row label="Régimen">
                {me.rucs[0]!.regimen === 'iva_irp' ? 'IVA + IRP' : 'IRE Simple'}
              </Row>
              <Row label="Validación">
                {me.rucs[0]!.validatedAt ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <ShieldCheck className="size-3.5" />
                    Validado contra SET
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-warning">
                    <Clock className="size-3.5" />
                    Pendiente
                  </span>
                )}
              </Row>
            </>
          )}

          <Separator className="my-2" />

          <Row label="Estado de la cuenta">
            <Badge
              variant={
                me.tenant.status === 'active'
                  ? 'success'
                  : me.tenant.status === 'onboarding'
                  ? 'info'
                  : me.tenant.status === 'paused'
                  ? 'warning'
                  : 'destructive'
              }
            >
              {me.tenant.status}
            </Badge>
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="size-4" />
              Datos de la empresa
            </CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowEditCompany(true)}>
            <Pencil />
            Editar
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {me.tenant.companyName ||
          me.tenant.companyAddress ||
          me.tenant.companyPhone ||
          me.tenant.companyEmail ||
          me.tenant.companyWebsite ||
          me.tenant.companySocials ? (
            <>
              <Row icon={<Building2 className="size-4" />} label="Nombre">
                {me.tenant.companyName ?? <span className="text-muted-foreground italic">Sin definir</span>}
              </Row>
              {me.tenant.companyAddress && (
                <Row icon={<MapPin className="size-4" />} label="Dirección">
                  {me.tenant.companyAddress}
                </Row>
              )}
              {me.tenant.companyPhone && (
                <Row icon={<Phone className="size-4" />} label="Teléfono">
                  {me.tenant.companyPhone}
                </Row>
              )}
              {me.tenant.companyEmail && (
                <Row icon={<Mail className="size-4" />} label="Email">
                  {me.tenant.companyEmail}
                </Row>
              )}
              {me.tenant.companyWebsite && (
                <Row icon={<Globe className="size-4" />} label="Web">
                  {me.tenant.companyWebsite}
                </Row>
              )}
              {me.tenant.companySocials && (
                <Row icon={<AtSign className="size-4" />} label="Redes">
                  {me.tenant.companySocials}
                </Row>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Todavía no cargaste los datos de tu empresa. Mientras tanto, los presupuestos usan tu
              nombre de cuenta como encabezado.
            </p>
          )}
        </CardContent>
      </Card>

      <SetAccessCard endpoint="/api/app/set-access" showProbe={false} />

      <AppFiscalProfileCard />

      {me.accountant && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserCircle className="size-4" />
              Tu contador
            </CardTitle>
            <CardDescription>El profesional que gestiona tu cuenta</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              <div className="font-medium">{me.accountant.fullName}</div>
              <div className="text-muted-foreground">{me.accountant.email}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {me.subscription && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="size-4" />
              Plan {me.subscription.plan === 'pro' ? 'Pro' : 'Esencial'}
            </CardTitle>
            <CardDescription>
              {me.subscription.billingCycle === 'annual' ? 'Facturación anual' : 'Facturación mensual'}
              {' · '}
              {me.subscription.status === 'trialing' ? 'En período de prueba' : me.subscription.status}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Próxima renovación:{' '}
              <strong className="text-foreground">
                {format(new Date(me.subscription.currentPeriodEnd), "d 'de' MMMM, yyyy", {
                  locale: es,
                })}
              </strong>
            </div>
          </CardContent>
        </Card>
      )}
      <AppProfileEditDialog
        open={showEditProfile}
        onOpenChange={setShowEditProfile}
        initial={{
          fullName: me.tenant.fullName,
          displayName: me.tenant.displayName,
          email: me.tenant.email,
        }}
      />
      <AppCompanyEditDialog
        open={showEditCompany}
        onOpenChange={setShowEditCompany}
        initial={{
          companyName: me.tenant.companyName,
          companyAddress: me.tenant.companyAddress,
          companyPhone: me.tenant.companyPhone,
          companyEmail: me.tenant.companyEmail,
          companyWebsite: me.tenant.companyWebsite,
          companySocials: me.tenant.companySocials,
        }}
      />
      <AppRucAddDialog open={showAddRuc} onOpenChange={setShowAddRuc} />
      {me.rucs[0] && (
        <AppRucEditDialog
          open={showEditRuc}
          onOpenChange={setShowEditRuc}
          ruc={{
            id: me.rucs[0].id,
            ruc: me.rucs[0].ruc,
            razonSocial: me.rucs[0].razonSocial,
            regimen: me.rucs[0].regimen,
          }}
        />
      )}
    </AppShell>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground inline-flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}
